/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ColDef, ColumnState} from 'ag-grid-community';
import {getSearchtFilter} from '@ion/applications/src/globalsearch/GlobalSearchUtils';
import {useGridRefreshAction} from '@ion/applications/src/hooks/useGridRefreshAction';
import {useGroupRefresh} from '@ion/applications/src/hooks/useGroupRefresh';
import {translate} from '@ion/core/src/locale/locale';
import {Account, AccountLink, CRUDPermissions, Link} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {
  booleanToString
} from '@ion/core/src/utils/BooleanUtils';
import {
  formatDateTime
} from '@ion/core/src/utils/DateUtil';
import {
  isANumber
} from '@ion/core/src/utils/NumberUtil';
import {
  dateSortComparator,
  numberSortComparator
} from '@ion/core/src/utils/SortUtils';
import {getLabel as getTimezoneLabel} from '@ion/dls/src/editors/TimezoneChooser';
import {
  defaultDateFilterParams,
  defaultNumberFilterParams,
  defaultStatusFilterParams,
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {
  definitionsToState
} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid, IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {
  useColumnChooser
} from '@ion/dls/src/grid/useColumnChooser';
import {
  useColumnState
} from '@ion/dls/src/grid/useColumnState';
import {GridPageSizeProps, ViewSubGroupsProps} from '@ion/dls/src/types/types';
import {
  determineColumnFilter,
  GridFilterType,
  processExportColumns
} from '@ion/dls/src/utils/GridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {exportGroups} from '../../../store/actions/exporter';
import {dispatchGridColumnsChange, sortColumns} from '../../../store/helpers';
import {GridOperations} from '../AdminContainer';
import {AccountCell} from './AccountCell';
import {GroupsRowEditor} from './GroupsRowEditor';
import {
  adminGroupGridRefreshed,
  getAdminGroups,
  NAME,
  SET_GRID_COLUMNS
} from './state';
import {
  adminGroupsAddResultSelector,
  adminGroupsBulkImportResultSelector,
  adminGroupsDestroyResultSelector,
  adminGroupsEditResultSelector,
  adminGroupsGridColumnStateSelector,
  adminGroupsModularSearchSelector
} from './state-selectors';
import SubGroupCell from './SubGroupCell';

export interface AdminGroupsProps extends GridOperations, ViewSubGroupsProps, GridPageSizeProps {
  onSelectAccount?: (value: AccountLink) => void;
  filteredColumns?: GroupsGridColumns[];
  Features?: any;
  searchKeyword?: string;
  allowSSOConfig?: boolean;
  allowUpdateSSOConfig?: boolean;
  isCtcAdmin?: boolean;
  includeAccountLink?: boolean;
  includeGlobalSearch?: boolean;
  enableSelectionsExport?: boolean;
  accountsPermissions?: CRUDPermissions;
  refreshInitialization?: Function;
  includeParentInExport?: boolean;
}

export enum GroupsGridColumns {
  NAME = 'name',
  TIMEZONE = 'timeZoneUI',
  PARENT = 'parentUI',
  STATUS = 'status',
  ACCOUNT_ID = 'accountId',
  ACTIVATED_ON = 'activatedOnUI',
  UNIT_OF_MEASURE = 'unitOfMeasure',
  ENTERPRISE = 'enterpriseUI',
  SOLOMON_ID = 'solomonId',
  SUBACCOUNTS = 'subAccounts',
  CREATED = 'createdOnUI',
  LAST_MODIFIED = 'lastModifiedOnUI',
  SUBSCRIPTION = 'subscriptionUI'
}

const DEFAULT_FILTERED_COLUMNS = [
  GroupsGridColumns.NAME,
  GroupsGridColumns.PARENT,
  GroupsGridColumns.STATUS,
  GroupsGridColumns.ACCOUNT_ID,
  GroupsGridColumns.SOLOMON_ID,
  GroupsGridColumns.LAST_MODIFIED
];

/**
 * Grid to be rendered on the Admin Groups page.
 */
export const AdminGroupsGrid = ({
  Features,
  isCtcAdmin,
  onSelectAccount,
  viewSubGroups,
  toggleViewSubGroups,
  setGridPageSize,
  accountsPermissions,
  refreshInitialization,
  gridPageSize = 20,
  searchKeyword = '',
  includeAccountLink = true,
  includeGlobalSearch = false,
  enableSelectionsExport = false,
  isSortByAlphaNumeric = false,
  hideSubGroups = false,
  allowSSOConfig = false,
  allowUpdateSSOConfig = false,
  usePreferedPageSize = false,
  includeParentInExport = false,
  filteredColumns = DEFAULT_FILTERED_COLUMNS
}: AdminGroupsProps, ref) => {
  const dispatch                              = useDispatch();
  const modularSearchParams                   = useSelector(adminGroupsModularSearchSelector);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const deleteResult                          = useSelector(adminGroupsDestroyResultSelector);
  const addResult                             = useSelector(adminGroupsAddResultSelector);
  const editResult                            = useSelector(adminGroupsEditResultSelector);
  const importResult                          = useSelector(adminGroupsBulkImportResultSelector);
  const columnState: ColumnState[]            = useSelector(adminGroupsGridColumnStateSelector);
  const ionGridRef                            = useRef<IonGridAPI>();
  const refreshGrid                           = useGroupRefresh();
  const defaultColumnDefinitions              = useMemo(() =>
    getColumnDefinitions({
      isAccountNameLink : !!onSelectAccount,
      isCtcAdmin,
      includeGlobalSearch,
      filteredColumns,
      isSortByAlphaNumeric
    }),
  [isCtcAdmin, includeGlobalSearch, filteredColumns, onSelectAccount]);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : defaultColumnDefinitions,
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  useGridRefreshAction(refreshRequired, adminGroupGridRefreshed);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    onExport,
    onRefresh
  }));

  const onColumnSelectionsUpdate = function(columns: ColDef[]): void {
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS));
  };

  const onColumnStateUpdate = function(state: ColumnState[]): void {
    if (!includeGlobalSearch) {
      dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS));
    }
  };

  useEffect(() => {
    const result = Boolean(
      deleteResult ||
      editResult ||
      addResult ||
      importResult
    );
    setRefreshRequired(result);
  }, [deleteResult, editResult, addResult, importResult]);

  useEffect(() => {
    if (includeGlobalSearch && searchKeyword) {
      setRefreshRequired(!refreshRequired);
    }
  }, [includeGlobalSearch, searchKeyword]);

  const attributeMappings = {
    accountId        : 'id',
    parentUI         : 'parent.name',
    subscriptionUI   : 'subscription',
    lastModifiedOnUI : 'lastModifiedOn'
  };

  const defaultSort: SortModelType = {
    colId : 'name',
    sort  : 'asc'
  };

  const getFilter = (filter) => {
    if (includeGlobalSearch) {
      const field = isANumber(searchKeyword) ? 'id' : 'name';
      return getSearchtFilter(field, searchKeyword, filter);
    }
    return filter;
  };

  function getGroups({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) {
    dispatch(getAdminGroups({
      includeSubGroups    : viewSubGroups as boolean,
      includeGlobalSearch : includeGlobalSearch,
      filter              : getFilter(filter),
      pageId              : page + 1,
      pageSize,
      sort,
      callback
    }));
  };

  function onEditGroups(group: Account) {

  }

  function onDeleteGroups(group: Account) {

  }

  const onExport = function(downloadParams?: DownloadTemplate) {
    const params = ionGridRef.current?.getExportParams(
      viewSubGroups as boolean,
      enableSelectionsExport,
      createModularSearchFilters(modularSearchParams)
    );
    if (downloadParams?.columns) {
      params.columns = processExportColumns(downloadParams?.columns);
    }
    params.includeParent = includeParentInExport;
    dispatch(exportGroups(params, downloadParams?.columns ? downloadParams : undefined));
  };

  const onRefresh = function() {
    setRefreshRequired(true);
  };

  return (
    <IonGrid ref={ionGridRef}
      omitHeader
      paginationEnabled
      showCustomPaginationBar
      pageSize={gridPageSize}
      setGridPageSize={setGridPageSize}
      refreshRequired={refreshRequired || (refreshGrid && !includeGlobalSearch)}
      setRefreshRequired={setRefreshRequired}
      loadItems={getGroups}
      augmentItems={constructGroupsGridRows}
      attributeMappings={attributeMappings}
      onBulkEdit={onEditGroups}
      onBulkDelete={onDeleteGroups}
      checkboxesEnabled={false}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      onColumnChooserSave={onColumnSelectionsUpdate}
      onColumnsUpdated={onColumnStateUpdate}
      onColumnChooserClose={function() {
        toggleColumnChooser(false);
      }}
      columnComponents={{
        rowMenu : item => (
          <GroupsRowEditor group={item?.data}
            showSSOConfig={allowSSOConfig && item?.data?.enterprise}
            allowUpdateSSOConfig={allowUpdateSSOConfig && item?.data?.enterprise}
            includeGlobalSearch={includeGlobalSearch}
            accountsPermissions={accountsPermissions}/>
        ),
        subGroup    : SubGroupCell,
        accountName : (item: GridResult) => (
          <AccountCell isAccountGrid
            item={item}
            isCtcAdmin={isCtcAdmin}
            Features={Features}
            refreshInitialization={refreshInitialization}
            includeAccountLink={includeAccountLink}
            onSelectAccount={onSelectAccount} />
        ),
        parentName : (item: any) => (
          <AccountCell isParentAccount
            item={item}
            isCtcAdmin={isCtcAdmin}
            Features={Features}
            refreshInitialization={refreshInitialization}
            onSelectAccount={onSelectAccount} />
        )
      }}
      pageSizes={PAGE_SIZES}
      toggleViewSubGroups={toggleViewSubGroups}
      viewSubGroups={viewSubGroups}
      hideSubGroups={hideSubGroups}
      defaultSort={defaultSort}
      usePreferedPageSize={usePreferedPageSize} />
  );
};

/**
 * Converts a collection of groups and converts them to a list of items specific
 * for display within the Admin Groups Grid, while retaining all of the items existing
 * attributes,
 */
export const constructGroupsGridRows = (groups: Account[], isExport = false): GridResult[] => {
  return groups.map(group => {
    const item = {
      ...group,
      accountId        : group?.id,
      parentUI         : group?.parent?.title,
      activatedOnUI    : formatDateTime(group?.activatedOn),
      timeZoneUI       : getTimezoneLabel(group?.timeZone),
      enterpriseUI     : booleanToString(group?.enterprise),
      subscriptionUI   : group?.subscription.title,
      createdOnUI      : formatDateTime(group?.createdOn),
      lastModifiedOnUI : formatDateTime(group?.lastModifiedOn)
    };
    if (isExport) {
      const items = group?.subAccounts as Link[];
      let parsed = '';
      items.forEach(({title}) => {
        parsed += `${title} `;
      });
      item.subAccounts = parsed;
    }
    return item;
  }) as GridResult[];
};

/**
 * Sets the custom renderer for the account name column cells in the grid, if the account
 * name is to be a link.
 */
const getAccountNameCellRenderer = (isAccountNameLink: boolean): string | undefined => (
  isAccountNameLink ? 'accountName' : undefined
);

/**
 * Sets custom renderer parameters for the account name column cells in the grid, if the account
 * name is to be a link.
 */
const getAccountNameCellRendererParams = (
  isAccountNameLink: boolean
): ({data}: {data: any;}) => {data: any;} | undefined => (
  isAccountNameLink ? ({data}) => ({data}) : undefined
);

const filterColumns = (columns, isSortByAlphaNumeric) => {
  return isSortByAlphaNumeric ? sortColumns(columns) : columns;
};

/**
 * Returns column definitions for the admin groups grid in  global search desktop mode.
 */
const getColumnDefinitionsDesktopSearch = (isAccountNameLink: boolean): ColDef[] => {
  return [{
    headerName   : translate('admin-groups:grid.columns.accountId'),
    field        : 'accountId',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultNumberFilterParams,
    comparator   : numberSortComparator
  }, {
    headerName         : translate('admin-groups:grid.columns.name'),
    field              : 'name',
    cellRenderer       : getAccountNameCellRenderer(isAccountNameLink),
    cellRendererParams : getAccountNameCellRendererParams(isAccountNameLink),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }, {
    headerName   : translate('admin-groups:grid.columns.status'),
    field        : 'status',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultStatusFilterParams
  }, {
    headerName         : translate('admin-groups:grid.columns.parentAccount'),
    field              : 'parentUI',
    cellRenderer       : 'parentName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }];
};

/**
 * Returns column definitions for the admin groups grid in desktop mode.
 */
const getColumnDefinitionsDesktop = ({
  isCtcAdmin,
  filteredColumns,
  isAccountNameLink,
  isSortByAlphaNumeric
}): ColDef[] => {
  const columns = [{
    headerName         : translate('admin-groups:grid.columns.name'),
    field              : GroupsGridColumns.NAME,
    cellRenderer       : getAccountNameCellRenderer(isAccountNameLink),
    cellRendererParams : getAccountNameCellRendererParams(isAccountNameLink),
    sortable           : true,
    filter             : determineColumnFilter(GroupsGridColumns.NAME, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }, {
    headerName : translate('admin-groups:grid.columns.timezone'),
    field      : GroupsGridColumns.TIMEZONE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }, {
    headerName         : translate('admin-groups:grid.columns.parentAccount'),
    field              : GroupsGridColumns.PARENT,
    cellRenderer       : 'parentName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : determineColumnFilter(GroupsGridColumns.PARENT, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }, {
    headerName   : translate('admin-groups:grid.columns.status'),
    field        : GroupsGridColumns.STATUS,
    sortable     : true,
    filter       : determineColumnFilter(GroupsGridColumns.STATUS, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultStatusFilterParams
  }, {
    headerName   : translate('admin-groups:grid.columns.accountId'),
    field        : GroupsGridColumns.ACCOUNT_ID,
    sortable     : true,
    filter       : determineColumnFilter(GroupsGridColumns.ACCOUNT_ID, filteredColumns, GridFilterType.NUMBER),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultNumberFilterParams,
    comparator   : numberSortComparator
  }, {
    headerName : translate('admin-groups:grid.columns.activatedOn'),
    field      : GroupsGridColumns.ACTIVATED_ON,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  }, {
    headerName : translate('admin-groups:grid.columns.unitOfMeasure'),
    field      : GroupsGridColumns.UNIT_OF_MEASURE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  }, {
    headerName : translate('admin-groups:grid.columns.enterpriseAccount'),
    field      : GroupsGridColumns.ENTERPRISE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  }, {
    headerName   : translate('admin-groups:grid.columns.solomonId'),
    field        : GroupsGridColumns.SOLOMON_ID,
    sortable     : true,
    filter       : determineColumnFilter(GroupsGridColumns.SOLOMON_ID, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  }, {
    headerName         : translate('admin-groups:grid.columns.subGroup'),
    field              : GroupsGridColumns.SUBACCOUNTS,
    cellRenderer       : 'subGroup',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  }, {
    headerName : translate('admin-groups:grid.columns.createdOn'),
    field      : GroupsGridColumns.CREATED,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  }, {
    headerName   : translate('admin-groups:grid.columns.lastModifiedOn'),
    field        : GroupsGridColumns.LAST_MODIFIED,
    sortable     : true,
    filter       : determineColumnFilter(GroupsGridColumns.LAST_MODIFIED, filteredColumns, GridFilterType.DATE),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultDateFilterParams,
    comparator   : dateSortComparator
  }];
  isCtcAdmin && columns.splice(1, 0, {
    headerName : translate('admin-groups:grid.columns.subscription'),
    field      : GroupsGridColumns.SUBSCRIPTION,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  });
  return filterColumns(columns, isSortByAlphaNumeric);
};

/**
 * Returns column definitions for the admin groups grid in mobile mode.
 */
const getColumnDefinitionsMobile = ({
  isCtcAdmin,
  filteredColumns,
  isAccountNameLink,
  isSortByAlphaNumeric
}): ColDef[] => {
  const columns = [{
    headerName         : translate('admin-groups:grid.columns.name'),
    field              : GroupsGridColumns.NAME,
    cellRenderer       : getAccountNameCellRenderer(isAccountNameLink),
    cellRendererParams : getAccountNameCellRendererParams(isAccountNameLink),
    sortable           : true,
    filter             : determineColumnFilter(GroupsGridColumns.NAME, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }, {
    headerName : translate('admin-groups:grid.columns.timezone'),
    field      : GroupsGridColumns.TIMEZONE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }, {
    headerName         : translate('admin-groups:grid.columns.parentAccount'),
    field              : GroupsGridColumns.PARENT,
    cellRenderer       : 'parentName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : determineColumnFilter(GroupsGridColumns.PARENT, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }, {
    headerName   : translate('admin-groups:grid.columns.status'),
    field        : GroupsGridColumns.STATUS,
    sortable     : true,
    filter       : determineColumnFilter(GroupsGridColumns.STATUS, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultStatusFilterParams
  }, {
    headerName   : translate('admin-groups:grid.columns.accountId'),
    field        : GroupsGridColumns.ACCOUNT_ID,
    sortable     : true,
    filter       : determineColumnFilter(GroupsGridColumns.ACCOUNT_ID, filteredColumns, GridFilterType.NUMBER),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultNumberFilterParams,
    comparator   : numberSortComparator
  }, {
    headerName : translate('admin-groups:grid.columns.activatedOn'),
    field      : GroupsGridColumns.ACTIVATED_ON,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  }, {
    headerName : translate('admin-groups:grid.columns.unitOfMeasure'),
    field      : GroupsGridColumns.UNIT_OF_MEASURE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  }, {
    headerName : translate('admin-groups:grid.columns.enterpriseAccount'),
    field      : GroupsGridColumns.ENTERPRISE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  }, {
    headerName   : translate('admin-groups:grid.columns.solomonId'),
    field        : GroupsGridColumns.SOLOMON_ID,
    sortable     : true,
    filter       : determineColumnFilter(GroupsGridColumns.SOLOMON_ID, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  }, {
    headerName         : translate('admin-groups:grid.columns.subGroup'),
    field              : GroupsGridColumns.SUBACCOUNTS,
    cellRenderer       : 'subGroup',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  }, {
    headerName : translate('admin-groups:grid.columns.createdOn'),
    field      : GroupsGridColumns.CREATED,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  }, {
    headerName   : translate('admin-groups:grid.columns.lastModifiedOn'),
    field        : GroupsGridColumns.LAST_MODIFIED,
    sortable     : true,
    filter       : determineColumnFilter(GroupsGridColumns.LAST_MODIFIED, filteredColumns, GridFilterType.DATE),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultDateFilterParams,
    comparator   : dateSortComparator
  }];
  isCtcAdmin && columns.splice(1, 0, {
    headerName : translate('admin-groups:grid.columns.subscription'),
    field      : GroupsGridColumns.SUBSCRIPTION,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  });
  return filterColumns(columns, isSortByAlphaNumeric);
};

const getColumnDefinitions = ({
  isCtcAdmin,
  includeGlobalSearch,
  filteredColumns,
  isAccountNameLink,
  isSortByAlphaNumeric
}) => {
  if (!includeGlobalSearch) {
    return {
      desktop : getColumnDefinitionsDesktop({
        isCtcAdmin,
        filteredColumns,
        isAccountNameLink,
        isSortByAlphaNumeric
      }),
      mobile : getColumnDefinitionsMobile({
        isCtcAdmin,
        filteredColumns,
        isAccountNameLink,
        isSortByAlphaNumeric
      })
    };
  }
  const definitions = getColumnDefinitionsDesktopSearch(isAccountNameLink);
  return {desktop: definitions, mobile: definitions};
};

export default forwardRef(AdminGroupsGrid);
